import { ConnectionStatus } from '../types';
import type { Message } from '../types';
import { Instant } from '@js-joda/core';
import { EnumCancelRejectionReason, EnumOrderUpdateType } from '@thinkalpha/trading-ws-client/contracts/order.js';
import type {
    CancelRejectionUpdate,
    Order,
    OrderExecutionUpdate,
    OrderUpdate,
    OrderUpdateReport,
} from '@thinkalpha/trading-ws-client/contracts/order.js';
import type { KeysWithoutReturnObjects, TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { distinct, filter, map, scan } from 'rxjs/operators';
import { io } from 'socket.io-client';
import { useContainer } from 'src/features/ioc';
import { useAccessToken } from 'src/hooks/state';
import { useObservableValue } from 'src/hooks/useObservableValue';
import { appConfig } from 'src/lib/config';
import { getUnderlyingSymbolFromInstrument, instrumentToString } from 'src/lib/instrumentUtils';
import { CONNECTION_OPTIONS } from 'src/lib/socketConnectionOptions';
import { bufferDebounce } from 'src/lib/util/rxjs';
import { UnreachableCaseError } from 'src/lib/util/unreachableCaseError';
import { container } from 'src/StaticContainer';
import { v4 } from 'uuid';

const log = container.get('Logger').getSubLogger({ name: 'useSubscribeToMessages' }, { component: 'trading' });

type OrderType = 'equity' | 'options' | 'multi';

/**
 * Get a string representing the order type for translation keys
 * Note: Not all order types need to be reflected here, only those that have different messages
 */
function getMessageOrderType(order: { side?: string; instrument?: { type?: string; symbol?: string } }): OrderType {
    // Check for multi-leg orders
    if (order.side === 'multi-leg') {
        return 'multi';
    }

    // Check for options by instrument type or symbol length
    if (order.instrument?.type === 'option' || (order.instrument?.symbol && order.instrument.symbol.length === 21)) {
        return 'options';
    }

    // Default to equity
    return 'equity';
}

export function deriveMessageFromOrder(order: Order, t: TFunction): Message | null {
    const id = v4();
    const { account, createdAt: timestamp, status, instrument } = order;

    const symbol = instrumentToString(instrument);
    const underlyingSymbol = getUnderlyingSymbolFromInstrument(instrument);
    const orderType = getMessageOrderType(order);

    // Create comprehensive context with all possible variables
    const messageContext = {
        side: order.side,
        quantity: order.quantity,
        quantityRemaining: order.quantity, // For orders, remaining equals quantity
        symbol,
        underlyingSymbol,
        price: order.price?.toString() ?? '',
        orderType: order.type,
        orderId: order.id ?? '',
        text: '',
        reason: '',
        accountId: account ?? '',
    };

    switch (status) {
        case 'pending': {
            const messageKey: KeysWithoutReturnObjects['json'] = order.price
                ? `trading::messages::orders::new::${orderType}::message`
                : `trading::messages::orders::new::${orderType}::messageMarket`;
            return {
                type: 'pending',
                accountId: account ?? '',
                id,
                timestamp,
                message: t(messageKey, messageContext),
                typeLabel: t('trading::messages::orders::new::typeLabel', messageContext),
                symbol,
            };
        }
        case 'created': // `created` status also is covered in order updates
        case 'submitted':
        case 'accepted':
        case 'updated':
        case 'kill-pending':
        case 'update-pending':
        case 'cancel-pending':
        case 'partially-filled':
        case 'filled':
        case 'canceled':
        case 'rejected':
        case 'suspended':
            return null;
        default: {
            throw new UnreachableCaseError(status);
        }
    }
}

export function deriveMessageFromOrderUpdate(order: OrderUpdateReport & OrderUpdate, t: TFunction): Message | null {
    const id = v4();

    const { accountId, timestamp, status, updateType, instrument, orderId, orderClOrdId, text } = order;
    const symbol = instrumentToString(instrument);
    const underlyingSymbol = getUnderlyingSymbolFromInstrument(instrument);

    const isExecutionUpdate = updateType !== EnumOrderUpdateType.cancelRejection;
    const executionOrder = isExecutionUpdate ? (order as OrderUpdateReport & OrderExecutionUpdate) : null;

    const orderType = getMessageOrderType({
        side: executionOrder?.side,
        instrument,
    });

    // Create comprehensive context with all possible variables
    const messageContext = {
        side: executionOrder?.side ?? '',
        quantity: executionOrder?.quantity ?? 0,
        quantityRemaining: executionOrder?.quantityRemaining ?? 0,
        symbol,
        underlyingSymbol,
        price: executionOrder?.price?.toString() ?? '',
        orderType: '',
        orderId: orderId ?? '',
        clOrdId: orderClOrdId ?? '',
        text: text ?? '',
        textSuffix: text ? `, ${text}` : '',
        reason: '',
        accountId: accountId ?? '',
    };

    switch (updateType) {
        case EnumOrderUpdateType.cancelRejection: {
            const { reason } = order as OrderUpdateReport & CancelRejectionUpdate;

            // Extract valid reason keys from translation file
            type CancelRejectionReasonKey =
                Extract<
                    KeysWithoutReturnObjects['json'],
                    `trading::messages::orders::cancelRejected::reasons::${string}`
                > extends `trading::messages::orders::cancelRejected::reasons::${infer Suffix}`
                    ? Suffix
                    : never;

            // Map the rejection reason to translation key using switch
            let reasonKey: CancelRejectionReasonKey = 'unknown';
            switch (reason) {
                case EnumCancelRejectionReason.TOO_LATE_TO_CANCEL:
                    reasonKey = 'toolate';
                    break;
                case EnumCancelRejectionReason.BROKER_OPTION:
                    reasonKey = 'brokerOption';
                    break;
                case EnumCancelRejectionReason.ORDER_ALREADY_IN_PENDING_CANCEL_OR_PENDING_REPLACE_STATUS:
                case EnumCancelRejectionReason.UNMATCHED_ACCOUNT_SYMBOL:
                case EnumCancelRejectionReason.UNKNOWN_ORDER:
                    reasonKey = 'unknown';
                    break;
                case EnumCancelRejectionReason.MANUAL_REVIEW:
                    reasonKey = 'pendingManualReview';
                    break;
                case undefined:
                case '':
                    // CxlRejReason (102) is optional on OrderCancelReject and the venue does
                    // reject without one; the contract fills '' for an absent reason.
                    log.warn('order reason is absent', { order });
                    reasonKey = 'unknown';
                    break;
                default:
                    throw new UnreachableCaseError(reason);
            }

            return {
                type: EnumOrderUpdateType.cancelRejection,
                typeLabel: t('trading::messages::orders::cancelRejected::typeLabel'),
                accountId: accountId ?? '',
                id,
                message: t(`trading::messages::orders::cancelRejected::reasons::${reasonKey}`, messageContext),
                timestamp,
                symbol,
            };
        }
        case EnumOrderUpdateType.status:
        case EnumOrderUpdateType.cancellation:
        case EnumOrderUpdateType.correction:
            switch (status) {
                case 'created':
                    return {
                        type: 'created',
                        accountId: accountId ?? '',
                        id,
                        timestamp,
                        message: t(`trading::messages::orders::acknowledged::${orderType}::message`, messageContext),
                        typeLabel: t('trading::messages::orders::acknowledged::typeLabel', messageContext),
                        symbol: symbol,
                    };
                case 'canceled':
                    return {
                        type: 'canceled',
                        accountId: accountId ?? '',
                        id,
                        timestamp,
                        message: t(`trading::messages::orders::canceled::${orderType}::message`, messageContext),
                        typeLabel: t('trading::messages::orders::canceled::typeLabel'),
                        symbol: symbol,
                    };
                case 'accepted':
                    return {
                        type: 'accepted',
                        accountId: accountId ?? '',
                        id,
                        timestamp,
                        message: t('trading::messages::orders::accepted::message', messageContext),
                        typeLabel: t('trading::messages::orders::accepted::typeLabel'),
                        symbol: symbol,
                    };
                case 'rejected':
                    return {
                        type: 'rejected',
                        accountId: accountId ?? '',
                        id,
                        timestamp,
                        message: t('trading::messages::orders::rejected::message', messageContext),
                        typeLabel: t('trading::messages::orders::rejected::typeLabel'),
                        symbol: symbol,
                    };
                case 'suspended':
                    return {
                        type: 'suspended',
                        accountId: accountId ?? '',
                        id,
                        timestamp,
                        message: t('trading::messages::orders::suspended::message', messageContext),
                        typeLabel: t('trading::messages::orders::suspended::typeLabel'),
                        symbol: symbol,
                    };
                case 'submitted':
                    return {
                        type: 'submitted',
                        accountId: accountId ?? '',
                        id,
                        message: t(`trading::messages::orders::submitted::${orderType}::message`, messageContext),
                        timestamp,
                        typeLabel: t('trading::messages::orders::submitted::typeLabel'),
                        symbol: symbol,
                    };
                case 'filled':
                    return {
                        type: 'filled',
                        accountId: accountId ?? '',
                        id,
                        message: t(`trading::messages::orders::filled::${orderType}::message`, messageContext),
                        typeLabel: t('trading::messages::orders::filled::typeLabel'),
                        timestamp,
                        symbol: symbol,
                    };
                case 'cancel-pending':
                    return {
                        type: 'cancel-pending',
                        typeLabel: t('trading::messages::orders::cancelPending::typeLabel'),
                        accountId: accountId ?? '',
                        id,
                        message: t(`trading::messages::orders::cancelPending::${orderType}::message`, messageContext),
                        timestamp,
                        symbol: symbol,
                    };
                case 'partially-filled':
                    return {
                        type: 'partially-filled',
                        accountId: accountId ?? '',
                        id,
                        message: t(`trading::messages::orders::partiallyFilled::${orderType}::message`, messageContext),
                        typeLabel: t('trading::messages::orders::partiallyFilled::typeLabel'),
                        timestamp,
                        symbol: symbol,
                    };
                case 'updated':
                    return {
                        type: 'updated',
                        accountId: accountId ?? '',
                        id,
                        message: t(`trading::messages::orders::updated::${orderType}::message`, messageContext),
                        typeLabel: t('trading::messages::orders::updated::typeLabel'),
                        timestamp,
                        symbol: symbol,
                    };
                case 'pending':
                case 'kill-pending':
                case 'update-pending':
                    return null;
                default:
                    throw new UnreachableCaseError(status);
            }
        default:
            throw new UnreachableCaseError(updateType);
    }
}

export function createConnectedMessage(accountId: string, t: TFunction): Message {
    const messageContext = {
        accountId,
    };

    return {
        type: ConnectionStatus.connected,
        id: v4(),
        accountId,
        timestamp: Instant.now(),
        message: t('trading::messages::connection::connected::message', messageContext),
        typeLabel: t('trading::messages::connection::connected::typeLabel', messageContext),
    };
}

export function createDisconnectedMessage(accountId: string, t: TFunction): Message {
    const messageContext = {
        accountId,
    };

    return {
        type: ConnectionStatus.disconnected,
        id: v4(),
        accountId,
        timestamp: Instant.now(),
        message: t('trading::messages::connection::disconnected::message', messageContext),
        typeLabel: t('trading::messages::connection::disconnected::typeLabel', messageContext),
    };
}

export function createReconnectedMessage(accountId: string, t: TFunction): Message {
    const messageContext = {
        accountId,
    };

    return {
        type: ConnectionStatus.reconnected,
        id: v4(),
        accountId,
        timestamp: Instant.now(),
        message: t('trading::messages::connection::reconnected::message', messageContext),
        typeLabel: t('trading::messages::connection::reconnected::typeLabel', messageContext),
    };
}

/**
 * Determines if an order update should be included based on account selection mode.
 * - Account Selection Mode: when accountIds has values, filter by orderUpdate.accountId
 * - My Activity Mode: when accountIds is null/empty, filter by orderUpdate.requestedBy matching currentUserId
 */
export function shouldIncludeOrderUpdate(
    orderUpdate: { accountId?: string; requestedBy?: string },
    accountIds: string[] | null,
    currentUserId: string | undefined,
): boolean {
    if (accountIds && accountIds.length > 0) {
        return accountIds.includes(orderUpdate.accountId ?? '');
    }
    return currentUserId !== undefined && currentUserId === orderUpdate.requestedBy;
}

/**
 * Determines if an order replacement should be included based on account selection mode.
 * - Account Selection Mode: when accountIds has values, filter by order.account
 * - My Activity Mode: when accountIds is null/empty, filter by order.creatorId matching currentUserId
 */
export function shouldIncludeOrderReplacement(
    order: { account?: string; creatorId?: string },
    accountIds: string[] | null,
    currentUserId: string | undefined,
): boolean {
    if (accountIds && accountIds.length > 0) {
        return accountIds.includes(order.account ?? '');
    }
    return currentUserId !== undefined && currentUserId === order.creatorId;
}

export function useSubscribeToMessages(accountIds: string[] | null) {
    const { t } = useTranslation();
    const socket = useMemo(() => io(appConfig.tradingApi, CONNECTION_OPTIONS), []);
    const container = useContainer();
    const messagesService = container.get('MessagesService');

    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [isConnected, setIsConnected] = useState<boolean | null>(null);

    const { token: accessToken } = useAccessToken() ?? { token: null };

    useEffect(() => {
        const handleConnected = () => {
            setIsConnected(true);
        };

        const handleDisconnect = () => {
            setIsConnected(false);
        };

        socket.on('connect', handleConnected);
        socket.on('disconnect', handleDisconnect);

        return () => {
            socket.off('connect', handleConnected);
            socket.off('disconnect', handleDisconnect);
            socket.disconnect();
        };
    }, [socket, setIsAuthenticated, setIsConnected]);

    useEffect(() => {
        if (isConnected === null) return;

        // when accounts are selected, send account-specific connection messages
        if (accountIds && accountIds.length > 0) {
            for (const accountId of accountIds) {
                if (isConnected) {
                    messagesService.messagesReceivedSubject$.next(createConnectedMessage(accountId, t));
                } else {
                    messagesService.messagesReceivedSubject$.next(createDisconnectedMessage(accountId, t));
                }
            }
        } else {
            // when no accounts are selected, send a connection message for all accounts
            if (isConnected) {
                messagesService.messagesReceivedSubject$.next(createConnectedMessage('all', t));
            } else {
                messagesService.messagesReceivedSubject$.next(createDisconnectedMessage('all', t));
            }
        }
    }, [isConnected, accountIds, messagesService, t]);

    useEffect(() => {
        if (!accessToken || !isConnected) {
            return;
        }

        socket.emit('auth', accessToken, (error?: string) => {
            if (error) {
                log.error({ message: 'Failed to authenticate to trading api websocket', error: new Error(error) });
                setIsAuthenticated(false);
                if (TRACE) {
                    log.trace({ message: 'auth ack - failure', error: new Error(error) });
                }
            } else {
                setIsAuthenticated(true);
                if (TRACE) {
                    log.trace({ message: 'auth ack - success' });
                }
            }
        });
    }, [socket, accessToken, isConnected]);

    useEffect(() => {
        const handleReconnect = () => {
            setIsConnected(true);
            // Send reconnection messages for specific accounts if we have them
            if (accountIds && accountIds.length > 0) {
                for (const accountId of accountIds) {
                    messagesService.messagesReceivedSubject$.next(createReconnectedMessage(accountId, t));
                }
            } else {
                // Send connection message for all accounts
                messagesService.messagesReceivedSubject$.next(createReconnectedMessage('all', t));
            }
        };

        const handleOrderReplacement = (orderUpdate: Order) => {
            const message = deriveMessageFromOrder(orderUpdate, t);
            if (!message) {
                return;
            }
            messagesService.messagesReceivedSubject$.next(message);
        };

        const handleOrderUpdated = (orderUpdate: OrderUpdateReport & OrderExecutionUpdate) => {
            if (accountIds && !accountIds.includes(orderUpdate.accountId ?? '')) {
                return;
            }

            const updateMessage = deriveMessageFromOrderUpdate(orderUpdate, t);
            if (updateMessage) {
                messagesService.messagesReceivedSubject$.next(updateMessage);
            }
        };

        socket.on('reconnect', handleReconnect);
        socket.on('order.updated', handleOrderUpdated);
        /** @deprecated Use order.snapshot */
        socket.on('order.replacement', handleOrderReplacement);
        socket.on('order.snapshot', handleOrderReplacement);

        return () => {
            socket.off('reconnect', handleReconnect);
            socket.off('order.updated', handleOrderUpdated);
            socket.off('order.replacement', handleOrderReplacement);
            socket.off('order.snapshot', handleOrderReplacement);
        };
    }, [socket, accountIds, setIsAuthenticated, setIsConnected, messagesService, t]);

    // whenever account filters change, leave all accounts and then join the new accounts
    useEffect(() => {
        if (!isAuthenticated || !isConnected) return;

        socket.emit('account.leave-all', (error?: string) => {
            if (error) {
                log.error({ message: 'Failed to leave all accounts', error: new Error(error) });
                if (TRACE) {
                    log.trace({ message: 'account.leave-all ack - failure', error: new Error(error) });
                }
                return;
            }

            if (TRACE) {
                log.trace({ message: 'account.leave-all ack - success' });
            }

            // After successfully leaving, join the new accounts (if any)
            if (accountIds && accountIds.length > 0) {
                for (const accountId of accountIds) {
                    socket.emit('account.join', { account: accountId }, (joinError?: string) => {
                        if (joinError) {
                            log.error({ message: 'Failed to join account', accountId, error: new Error(joinError) });
                            if (TRACE) {
                                log.trace({
                                    message: 'account.join ack - failure',
                                    accountId,
                                    error: new Error(joinError),
                                });
                            }
                        } else if (TRACE) {
                            log.trace({ message: 'account.join ack - success', accountId });
                        }
                    });
                }
            }
        });
    }, [socket, accountIds, isConnected, isAuthenticated]);

    const messageStream$ = useMemo(
        () =>
            messagesService.messagesReceivedSubject$.pipe(
                distinct((message) => message.id),
                bufferDebounce(100),
                filter((x) => x.length > 0),
                scan((acc, batch) => {
                    const newAcc = new Map(acc);
                    for (const message of batch) {
                        newAcc.set(message.accountId, [message, ...(newAcc.get(message.accountId) ?? [])]);
                    }
                    return newAcc;
                }, new Map<string, Message[]>()),
            ),
        [messagesService.messagesReceivedSubject$],
    );

    const accountIdsList = useMemo(() => accountIds ?? [], [accountIds]);

    return useObservableValue(
        () =>
            messageStream$.pipe(
                map((x) => {
                    // If no account filter is selected, return all messages
                    if (!accountIds || accountIds.length === 0) {
                        return x.values().flatten().toArray();
                    }
                    // Otherwise, return messages for all selected accounts
                    const allMessagesForSelectedAccounts: Message[] = [];
                    for (const accountId of accountIdsList) {
                        const messagesForAccount = x.get(accountId);
                        if (messagesForAccount) {
                            allMessagesForSelectedAccounts.push(...messagesForAccount);
                        }
                    }
                    return allMessagesForSelectedAccounts.length > 0 ? allMessagesForSelectedAccounts : undefined;
                }),
                filter((x): x is NonNullable<typeof x> => x !== undefined),
            ),
        [],
        [accountIds],
    );
}
